import { forwardRef, type SelectHTMLAttributes } from 'react';
import { Select as SelectPrimitive } from 'radix-ui';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/**
 * Was Radix als `value` und `defaultValue` bekommt. Nimmt die Props als Stück,
 * damit *beide* Teile der Regel ohne geöffnetes Menü prüfbar sind — auch der,
 * an dem es schiefging: wann das Feld überhaupt als gesteuert gilt.
 *
 * Gesteuert ist es, sobald `value` in den Props **steht** — auch als
 * `undefined`. Deshalb `'value' in props` und nicht `value !== undefined`:
 * `value={grund || undefined}` ist ein gesteuertes Feld, das gerade leer ist,
 * kein ungesteuertes. Ein leerer oder unbekannter Wert (Altdaten, gelöschte
 * Kategorie) geht dann als `''` durch, und Radix zeigt den Platzhalter.
 *
 * Der Grund steht als Fehler in der Geschichte: Bis Oktober 2026 reichte das
 * Feld ein `undefined` unverändert weiter. Radix nimmt ein fehlendes `value`
 * als „ungesteuert“ und behält dann den zuletzt gewählten Eintrag. Im
 * Zonen-Panel zeigte Fläche B so den Verschmutzungsgrad von Fläche A, während
 * `null` gespeichert wurde — ein Pflichtfeld, das gefüllt aussah und leer war
 * (Befund B-8).
 *
 * Dass die Entscheidung bis zum Nutzer durchkommt, prüft
 * `pm-web/src/features/projects/components/AblehnenDialog.test.tsx` durch Radix
 * hindurch: Das Muster `value={x || undefined}` schreibt dort der einzige
 * Aufrufer im Haus. Sichtbar wird der Unterschied erst, wenn ausgewählt wurde
 * und das Prop danach verschwindet, ohne dass das Feld ausgebaut wird — hier
 * sitzt die Regel also als Wache, nicht als laufende Reparatur.
 */
export function selectWerte(props: Pick<SelectProps, 'options' | 'value' | 'defaultValue'>): {
  value?: string;
  defaultValue?: string;
} {
  const { options, value, defaultValue } = props;
  const gueltig = (v: string | undefined) => v !== undefined && v !== '' && options.some((o) => o.value === v);
  if ('value' in props) return { value: gueltig(value) ? value : '' };
  if (defaultValue === undefined) return {};
  return { defaultValue: gueltig(defaultValue) ? defaultValue : '' };
}

/**
 * Auswahl aus einer festen Liste ohne Suche. Bis fünf gleichwertige Werte besser als
 * SegmentedControl oder ChipGroup. (Ergänzung nach shadcn/ui, nicht im Design System.)
 */
export function Select(props: SelectProps) {
  // `value` und `defaultValue` werden hier nur herausgenommen, damit sie nicht
  // im `...aria` am Trigger landen — gelesen werden sie aus `props`, weil
  // `selectWerte` wissen muss, ob `value` überhaupt dasteht (siehe oben).
  const {
    options,
    value: _value,
    defaultValue: _defaultValue,
    onChange,
    placeholder = 'Auswählen',
    disabled,
    size = 'md',
    id,
    name,
    className,
    ...aria
  } = props;
  return (
    <SelectPrimitive.Root
      {...selectWerte(props)}
      onValueChange={onChange}
      disabled={disabled}
      name={name}
    >
      <SelectPrimitive.Trigger
        id={id}
        {...aria}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-muted text-left text-foreground transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:border-ring data-[state=open]:ring-1 data-[state=open]:ring-ring',
          'aria-invalid:border-danger disabled:cursor-not-allowed disabled:opacity-45 data-[placeholder]:text-muted-foreground',
          // sm ist auf Touch-Geräten so hoch wie md (44 px): 36 px trifft man mit dem Daumen schlecht.
          size === 'sm' ? 'h-control-sm pl-2.5 pr-1.5 text-field-sm touch:h-control-md' : 'h-control-md pl-3 pr-2 text-field',
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="text-muted-foreground" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className="z-[2000] max-h-[min(20rem,var(--radix-select-content-available-height))] w-[var(--radix-select-trigger-width)] min-w-40 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md animate-ek-pop"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex h-8 cursor-default select-none items-center gap-2 rounded-sm pl-2 pr-8 text-sm outline-none touch:h-11 data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-45"
              >
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-2">
                  <Check size={16} strokeWidth={1.75} aria-hidden className="text-highlight" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export interface NativeSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  /** Erste, nicht wählbare Zeile bei leerem Wert. */
  placeholder?: string;
  /** Klassen für die Hülle (nicht das <select>). */
  className?: string;
}

/**
 * Natives <select> in der Optik von Select, mit ref: für react-hook-form
 * (`register`, `Controller`) und die Auswahl des Betriebssystems am Handy.
 * (Ergänzung, nicht im Design System.)
 */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { options, placeholder, className, value, ...rest },
  ref,
) {
  const unbekannt = value !== undefined && placeholder !== undefined && !options.some((o) => o.value === String(value));
  return (
    <div className={cn('relative w-full', className)}>
      <select
        ref={ref}
        value={unbekannt ? '' : value}
        {...rest}
        className={cn(
          'h-control-md w-full cursor-pointer appearance-none rounded-md border border-input bg-muted pl-3 pr-9 text-field text-foreground transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'aria-invalid:border-danger disabled:cursor-not-allowed disabled:opacity-45 has-[option[value=""]:checked]:text-muted-foreground',
        )}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
});
